/**
 * Generic inquiry modal — handles Apply Now and Partner With Us flows.
 * Variants differ by fields, labels, and endpoint.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VARIANTS = {
  apply: {
    endpoint: "/apply",
    tag: "Apply Now",
    title: (
      <>
        Step into your <em className="text-[#D4AF37] not-italic italic">future</em>.
      </>
    ),
    intro: "Tell us about your journey and which program fits your goals. Our admissions team reviews every application personally and reaches out within 5 business days.",
    testid: "apply",
    initial: {
      full_name: "",
      email: "",
      phone: "",
      city: "",
      experience: "Aspiring",
      interest: "The Braid Summit Academy",
      message: "",
    },
    fields: [
      { key: "full_name", label: "Full Name *", placeholder: "Your full name", required: true, col: 2 },
      { key: "email", label: "Email *", type: "email", placeholder: "you@email.com", required: true, col: 1 },
      { key: "phone", label: "Phone", type: "tel", placeholder: "(555) 555-5555", col: 1 },
      { key: "city", label: "City / State", placeholder: "Atlanta, GA", col: 1 },
      {
        key: "experience",
        label: "Experience Level *",
        type: "select",
        required: true,
        col: 1,
        options: ["Aspiring", "Beginner", "Intermediate", "Advanced", "Professional"],
      },
      {
        key: "interest",
        label: "Program of Interest *",
        type: "select",
        required: true,
        col: 2,
        options: [
          "The Braid Summit Academy",
          "Youth Development Programs",
          "Workshops & Events",
          "Mentorship",
          "Not Sure Yet",
        ],
      },
      { key: "message", label: "Tell us about yourself", type: "textarea", rows: 4, col: 2 },
    ],
    submitLabel: "Submit Application",
    successTitle: (name) => `Welcome, ${name || "friend"}.`,
    successBody: (form) =>
      `We've received your application for ${form.interest}. Our admissions team will personally review your submission and reach out within 5 business days.`,
  },
  partner: {
    endpoint: "/partner",
    tag: "Partner With Us",
    title: (
      <>
        Build the <em className="text-[#D4AF37] not-italic italic">future</em> with us.
      </>
    ),
    intro: "Tell us about your organization and the kind of collaboration you envision. Our partnerships team will reach out within 3 business days with proposed activation ideas.",
    testid: "partner",
    initial: {
      organization: "",
      contact_name: "",
      email: "",
      phone: "",
      partnership_type: "Brand",
      message: "",
    },
    fields: [
      { key: "organization", label: "Organization *", placeholder: "Acme Beauty Co.", required: true, col: 2 },
      { key: "contact_name", label: "Contact Name *", placeholder: "Jane Doe", required: true, col: 1 },
      { key: "phone", label: "Phone", type: "tel", placeholder: "(555) 555-5555", col: 1 },
      { key: "email", label: "Email *", type: "email", placeholder: "you@company.com", required: true, col: 2 },
      {
        key: "partnership_type",
        label: "Partnership Type *",
        type: "select",
        required: true,
        col: 2,
        options: ["Brand", "School / University", "Civic / Government", "Nonprofit", "Media / Press", "Other"],
      },
      { key: "message", label: "How would you like to collaborate?", type: "textarea", rows: 4, col: 2 },
    ],
    submitLabel: "Submit Inquiry",
    successTitle: (name) => `Thank you, ${name || "friend"}.`,
    successBody: (form) =>
      `We've received your partnership inquiry from ${form.organization}. Our team will review your message and respond within 3 business days with proposed collaboration ideas.`,
  },
};

const InquiryModal = ({ open, variant, onClose }) => {
  const cfg = VARIANTS[variant];
  const [form, setForm] = useState(cfg ? cfg.initial : {});
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open && cfg) {
      document.body.style.overflow = "hidden";
      setForm(cfg.initial);
      setStatus("idle");
      setErrorMsg("");
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, cfg]);

  if (!cfg) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}${cfg.endpoint}`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  const firstName = (form.full_name || form.contact_name || "").split(" ")[0];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto"
          onClick={onClose}
          data-testid={`${cfg.testid}-modal-overlay`}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35 }}
            className="relative w-full max-w-2xl bg-[#15110a] border border-[#D4AF37]/40 my-8 sm:my-0"
            onClick={(e) => e.stopPropagation()}
            data-testid={`${cfg.testid}-modal`}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/60 hover:text-[#D4AF37] transition-colors"
              aria-label="Close"
              data-testid={`${cfg.testid}-modal-close`}
            >
              <X size={22} />
            </button>

            <div className="p-8 sm:p-12">
              {status === "success" ? (
                <div className="text-center py-8" data-testid={`${cfg.testid}-success`}>
                  <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37] flex items-center justify-center">
                    <Check size={28} className="text-[#D4AF37]" />
                  </div>
                  <p className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase mb-4">Submission Received</p>
                  <h3 className="font-display text-3xl sm:text-4xl mb-4 leading-tight">{cfg.successTitle(firstName)}</h3>
                  <p className="text-white/65 leading-relaxed max-w-md mx-auto">{cfg.successBody(form)}</p>
                  <button
                    onClick={onClose}
                    className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-black px-8 py-3 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-px bg-[#D4AF37]" />
                    <span className="text-[#D4AF37] text-[10px] tracking-[0.4em] uppercase">{cfg.tag}</span>
                  </div>
                  <h3 className="font-display text-3xl sm:text-4xl mb-3 leading-tight">{cfg.title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed mb-8">{cfg.intro}</p>

                  <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {cfg.fields.map((f) => (
                      <div key={f.key} className={f.col === 2 ? "sm:col-span-2" : ""}>
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">{f.label}</label>
                        {f.type === "select" ? (
                          <select
                            required={f.required}
                            value={form[f.key] || ""}
                            onChange={update(f.key)}
                            data-testid={`${cfg.testid}-${f.key}`}
                            className="w-full bg-[#15110a] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white transition-colors"
                          >
                            {f.options.map((o) => (
                              <option key={o} value={o} className="bg-[#15110a]">{o}</option>
                            ))}
                          </select>
                        ) : f.type === "textarea" ? (
                          <textarea
                            rows={f.rows || 4}
                            value={form[f.key] || ""}
                            onChange={update(f.key)}
                            placeholder={f.placeholder}
                            data-testid={`${cfg.testid}-${f.key}`}
                            className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder-white/30 resize-none transition-colors"
                          />
                        ) : (
                          <input
                            type={f.type || "text"}
                            required={f.required}
                            value={form[f.key] || ""}
                            onChange={update(f.key)}
                            placeholder={f.placeholder}
                            data-testid={`${cfg.testid}-${f.key}`}
                            className="w-full bg-transparent border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-sm text-white placeholder-white/30 transition-colors"
                          />
                        )}
                      </div>
                    ))}

                    {status === "error" && (
                      <p className="sm:col-span-2 text-xs text-red-400 tracking-wide" data-testid={`${cfg.testid}-error`}>{errorMsg}</p>
                    )}

                    <div className="sm:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-2">
                      <button
                        type="submit"
                        disabled={status === "loading"}
                        data-testid={`${cfg.testid}-submit`}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#b8860b] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      >
                        {status === "loading" ? (
                          <><Loader2 size={16} className="animate-spin" /> Sending</>
                        ) : (
                          <>{cfg.submitLabel} <ArrowUpRight size={14} /></>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium text-white/60 hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                    </div>

                    <p className="sm:col-span-2 text-[10px] text-white/30 tracking-wide leading-relaxed mt-2">
                      By submitting, you agree to be contacted by The Braid Summit team. We respect your privacy and never share your information.
                    </p>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InquiryModal;
